<template>
	<div>
		<ListLayout
			:title="$t('admin_categories_title')"
			:text="$t('admin_categories_title')"
			:placeholder="$t('admin_categories_search_action')"
			@search="onSearch"
		>
			<template #button>
				<Button primary @click="launchNewCategoryModal">
					{{ $t('admin_categories_new_category_title') }}
				</Button>
			</template>

			<template #table>
				<Table
					:columns="columns"
					:rows="categories"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:loadingOverlay="loading"
					:loadingMore="loadingMore"
					:defaultSort="sort"
					headerSize="small"
					@sort="onSort"
					@load-more="loadMore"
					@row-click="goToCategoryDetails"
					loadMoreTrigger="400px"
					height="calc(100vh - 208px)"
					ref="table"
				>
					<template #cell-created_at="{ value }">
						{{ $m(value).format('MM/DD/YYYY') }}
					</template>

					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="menuOptions"
							@click.stop
							@select="onContextMenuItemClick($event, data)"
						/>
					</template>

					<template #empty>
						<EmptyState
							v-if="!query"
							:icon="CategoriesIcon"
							:text="$t('admin_categories_organize_groups_desc')"
						>
							<template #button>
								<Button primary @click="launchNewCategoryModal">
									{{ $t('admin_categories_new_category_title') }}
								</Button>
							</template>
						</EmptyState>
						<NoSearchResults v-if="query" :query="query" />
					</template>
				</Table>
			</template>
		</ListLayout>

		<RenameCategoryModal
			v-if="categoryToRename"
			:category="categoryToRename"
			@close="categoryToRename = null"
			@success="fetchCategories"
		/>

		<NewCategoryModal
			v-if="showNewCategoryModal"
			showSuccess
			@create="fetchCategories"
			@close="showNewCategoryModal = false"
			ref="newModal"
		/>
	</div>
</template>

<script>
import {
	Table,
	Button,
	closeAllMenus,
	DotsMenu,
	ASC,
} from '@/components/common'
import { CategoriesIcon } from '@/icons'
import RenameCategoryModal from './RenameCategoryModal'
import NewCategoryModal from './NewCategoryModal'
import debounce from 'debounce'
import { ListLayout, EmptyState, NoSearchResults, get } from '../common'
import { storage } from '@/helpers/utils'

const PAGE_SIZE = 30

export default {
	name: 'Categories',
	components: {
		Table,
		Button,
		DotsMenu,
		NewCategoryModal,
		RenameCategoryModal,
		ListLayout,
		EmptyState,
		NoSearchResults,
	},

	data() {
		return {
			categoryToRename: null,
			categories: [],
			loading: true,
			loadingMore: false,
			fetchError: false,
			query: this.$route.query.q || null,
			CategoriesIcon,
			columns: [
				{
					field: 'title',
					label: 'admin_categories_category_name_label',
					sorter: true,
					ascSort: 'title',
					dscSort: '-title',
				},
				{
					field: 'created_at',
					label: 'admin_categories_created_on_label',
					width: '600px',
					sorter: true,
					ascSort: 'created_at',
					dscSort: '-created_at',
				},
				{
					field: 'chats_count',
					label: 'admin_categories_groups_label',
					width: '600px',
				},
				{
					field: 'actions',
					width: '100px',
					align: 'right',
				},
			],
			menuOptions: [
				{
					id: 'details',
					label: 'admin_categories_open_category_details_action',
				},
				{ id: 'rename', label: 'admin_categories_rename_category_action' },
			],
			showNewCategoryModal: false,
			sort: { field: 'title', param: 'title', order: ASC },
		}
	},

	created() {
		this.fetchCategoriesDebounced = debounce(
			this.fetchCategories.bind(this),
			500
		)
	},

	mounted() {
		this.fetchCategories()
		this.checkQueryParams()
	},

	async activated() {
		// if some category was modified on another screen, refetch the list
		if (storage.get('org_mgmt_changed_categories')) {
			storage.remove('org_mgmt_changed_categories')
			await this.fetchCategories()
		}
	},

	methods: {
		launchNewCategoryModal() {
			this.showNewCategoryModal = true
		},

		onContextMenuItemClick({ id }, category) {
			switch (id) {
				case 'rename':
					this.categoryToRename = category
					break
				case 'details':
					this.goToCategoryDetails(category)
					break
			}
		},

		goToCategoryDetails({ id }) {
			closeAllMenus()
			this.$router.push({ name: 'category', params: { id } })
		},

		checkQueryParams() {
			const { query } = this.$route
			if (query.new || query.q) {
				const newQuery = Object.assign({}, query)
				if (query.new) {
					this.$refs.newModal.openNewModal()
					delete newQuery.new
				}
				if (query.q) {
					delete newQuery.q
				}
				this.$router.replace({ query: newQuery })
			}
		},

		onSort(sort, { ascSort, dscSort }) {
			this.sort = {
				...sort,
				param: sort.order === ASC ? ascSort : dscSort,
			}
			this.fetchCategories()
		},

		async fetchCategories() {
			this.loading = true
			this.fetchError = false
			const params = { 'page[size]': PAGE_SIZE }

			if (this.query) {
				params['filter[search]'] = encodeURIComponent(this.query)
			}

			if (this.sort) {
				params['sort'] = this.sort.param
			}

			try {
				this.apiResponse = await get('/organization/categories', params)
				this.categories = this.apiResponse.data
				this.$refs.table?.scrollToTop()
			} catch (e) {
				console.error(e)
				this.fetchError = true
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loading = false
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.categories = this.categories.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},

		onSearch(query) {
			this.query = query
			this.loading = true
			this.fetchCategoriesDebounced()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$header-height: 170px;

header {
	background: #f9f9f9;
	height: $header-height;
	@include flex-column;
	padding: 24px;
	.title {
		@include flex-spread;
		margin: 0 0 24px 0;
		h1 {
			font-family: Avenir-Heavy;
			font-size: 36px;
			margin: 0;
			color: var(--neutral-800);
		}
	}
	.search-bar {
		background: white;
	}
}

.modal-wrapper {
	padding: 24px;
}

.empty-state {
	@include flex-column-center;
	.pronto-icon {
		font-size: 75px;
		color: var(--blue-300);
		margin-bottom: 24px;
	}
	.pronto-btn {
		margin-top: 24px;
	}
}

.no-search-results {
	@include flex-column-center;
	font-size: 24px;
	color: var(--neutral-800);
	.pronto-icon {
		font-size: 75px;
		color: var(--blue-300);
		margin-bottom: 24px;
	}
}
</style>
